/**
 * A distinct error so callers can differentiate a timeout from other errors.
 */
export class TimeoutError extends Error {}

/**
 * Reject with a TimeoutError if `promise` takes longer than `timeoutMs`.
 * The underlying promise continues to run, we just stop waiting for it.
 *
 * @param promise - The promise to bound.
 * @param timeoutMs - Milliseconds to wait before giving up.
 * @param message - Message for the TimeoutError.
 * @returns The resolved value of `promise`, if it settles in time.
 * @throws {TimeoutError} If `promise` does not settle within `timeoutMs`.
 */
export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  message = 'Operation timed out',
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      reject(new TimeoutError(message));
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};
